import {
  Component,
  forwardRef,
  Input,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface ChipItem {
  display: string;
  value: string;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector -- in-house drop-in that kept the replaced library's template API
    selector: 'tag-input',
    template: `
    <div
      class="chip-input-container form-control"
      [ngClass]="theme"
      (click)="focusInput()"
      >
      @for (item of items; track item; let i = $index) {
        <span class="chip">
          <span class="chip-label">{{ item.display }}</span>
          <button
            type="button"
            class="chip-remove"
            (click)="removeItem(i)"
            aria-label="Remove"
            >
            &times;
          </button>
        </span>
      }
      <input
        #chipInput
        class="chip-text-input"
        [placeholder]="placeholder"
        (keydown)="onKeyDown($event)"
        (blur)="onBlur()"
        />
    </div>
    `,
    styles: [
        `
      .chip-input-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.15rem;
        min-height: calc(3.5rem + 2px);
        cursor: text;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        background-color: #e9ecef;
        border: 1px solid #ced4da;
        border-radius: 0.25rem;
        padding: 0.15rem 0.4rem;
        margin: 0.15rem;
        font-size: 0.875rem;
        line-height: 1.4;
      }
      .chip-label {
        margin-right: 0.3rem;
      }
      .chip-remove {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1rem;
        line-height: 1;
        padding: 0 0.15rem;
        color: #6c757d;
      }
      .chip-remove:hover {
        color: #dc3545;
      }
      .chip-text-input {
        flex: 1 1 auto;
        border: none;
        outline: none;
        min-width: 80px;
        padding: 0;
        font-size: inherit;
        color: inherit;
        background: transparent;
      }
      .chip-text-input::placeholder {
        color: #6c757d;
      }
    `
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChipInputComponent implements ControlValueAccessor {
  @ViewChild('chipInput') chipInputRef: ElementRef<HTMLInputElement>;

  @Input() placeholder = '';
  @Input() theme = 'bootstrap';
  @Input() maxItems = 0;
  @Input() separatorKeyCodes: number[] = [13, 188]; // Enter and comma

  items: ChipItem[] = [];

  private onChange: (value: ChipItem[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: ChipItem[] | null): void {
    this.items = value && Array.isArray(value) ? [...value] : [];
  }

  registerOnChange(fn: (value: ChipItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.separatorKeyCodes.includes(event.keyCode)) {
      event.preventDefault();
      this.addChipFromInput();
    }

    // Backspace on empty input removes last chip
    if (
      event.keyCode === 8 &&
      !this.chipInputRef.nativeElement.value &&
      this.items.length > 0
    ) {
      this.removeItem(this.items.length - 1);
    }
  }

  focusInput(): void {
    this.chipInputRef?.nativeElement.focus();
  }

  onBlur(): void {
    this.addChipFromInput();
    this.onTouched();
  }

  removeItem(index: number): void {
    this.items.splice(index, 1);
    this.onChange([...this.items]);
  }

  private addChipFromInput(): void {
    const input = this.chipInputRef.nativeElement;
    const text = input.value.trim();

    if (!text) {
      return;
    }

    if (this.maxItems > 0 && this.items.length >= this.maxItems) {
      return;
    }

    // Avoid duplicates
    if (this.items.some((item) => item.value === text)) {
      input.value = '';
      return;
    }

    this.items.push({ display: text, value: text });
    this.onChange([...this.items]);
    input.value = '';
  }
}
